// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm

import type { ProductDataLayer } from "~~/models/products";

export interface CreativeEventData {
  creativeTitle: string | undefined;
  creativeFileTitle: string | undefined | null;
  creativeFileName: string;
  creativeHyperlink?: string | undefined | null;
  creativeCategoryCode?: string | undefined;
  creativeProductCode?: string | undefined;
  creativeBrandName?: string | undefined | null;
}

// https://support.google.com/analytics/answer/9267735?hl=en
type PredefinedEventNames
  = "add_payment_info"
    | "add_shipping_info"
    | "add_to_cart"
    | "add_to_wishlist"
    | "begin_checkout"
    | "purchase"
    | "remove_from_cart"
    | "select_item"
    | "select_promotion"
    | "view_cart"
    | "view_item"
    | "view_item_list"
    | "view_promotion";

type CustomEventNames
  = "activate-account"
    | "change-password"
    | "forgot-password"
    | "impersonate"
    | "reset-password"
    | "search-suggestion-fetch"
    | "search-suggestion-select"
    | "template-list-create"
    | "template-list-rename"
    | "template-list-delete"
    | "visit-dev-page"
;

export interface TrackProductInfo {
  dataLayer: ProductDataLayer;
  /** The product quantity. */
  quantity: number;
  /** Don't include shipping or tax. */
  unitPriceGross: number;
  /** The discount amount per unit, excl. tax (optional). */
  discountAmount?: number;
  /** The currency code (optional). */
  currencyCode?: string;
  /** The attribution list (optional). */
  listName?: "Checkout" | "Details" | "Orders" | "Results" | "Templates" | null | undefined;
  /** Position in the list (optional). */
  index?: number;
}

export interface TransactionInfo {
  /** The transaction ID. */
  transactionId: string;
  /** The monetary value of the event. Don't include shipping or tax. */
  totalGross: number;
  /** The currency code. If not set, the default currency code will be used. */
  currency?: string;
  /** The optional shipping cost. */
  shippingCost?: number;
  /** The optional tax amount. */
  tax?: number;

  products: Array<ProductDataLayer>;
};

class Ga4Product {
  /**
   * Item ID (context-specific).
   * One of item_id or item_name is required for product or impression data.
   */
  public item_id!: string;

  /**
   * Item ID (context-specific).
   * One of item_id or item_name is required for product or impression data.
   */
  public item_name!: string;

  /**
   * Item quantity.
   */
  public quantity?: number;

  /**
   * A product affiliation to designate a supplying company or brick and mortar store location.
   */
  public affiliation?: string;

  /**
   * Coupon code used for a purchase.
   */
  public coupon?: string;

  /**
   * The monetary price of the item per sales unit, in the specified currency parameter.
   */
  public price?: number | null;

  /**
   * Monetary value of discount associated with a purchase.
   */
  public discount?: number;

  /**
   * Item brand
   */
  public item_brand?: string;

  /**
   * Item Category (context-specific).
   */
  public item_category?: string;

  /**
   * Item Category (context-specific). item_category2 can also be used if the item has many categories.
   */
  public item_category2?: string;

  /**
   * Item Category (context-specific). item_category3 can also be used if the item has many categories.
   */
  public item_category3?: string;

  /**
   * Item Category (context-specific). item_category4 can also be used if the item has many categories.
   */
  public item_category4?: string;

  /**
   * Item Category (context-specific). item_category5 can also be used if the item has many categories.
   */
  public item_category5?: string;

  /**
   * The item variant or unique code or description for additional item details/options.
   */
  public item_variant?: string | null;

  /**
   * The ID of the list in which the item was presented to the user.
   */
  public item_list_id?: string | null;
  /**
   * The name of the list in which the item was presented to the user.
   */
  public item_list_name?: string | null;

  /**
   * The ID of the promotion associated with the item.
   */
  public promotion_id?: string | null;
  /**
   * The name of the promotion associated with the item.
   */
  public promotion_name?: string | null;

  /**
   * The index of the item in a list.
   */
  public index?: number | null;
}

// https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
export function useGtmTracking() {
  const { proxy } = useScriptGoogleTagManager();

  // TODO refactor from PlanktonProductDataLayer to specific class with the analytics properties?
  // TODO that would make the composable more reusable
  function convertToGA4(dto: ProductDataLayer): Ga4Product {
    const item = {
      item_id: dto.productNumber!,
      item_name: dto.name!,
      item_brand: dto.brand || undefined,
      affiliation: dto.affiliation || undefined,
      promotion_id: dto.promotionId || undefined,
      promotion_name: dto.promotionName || undefined,
      quantity: dto.quantity || 1,
      price: dto.price,
      index: dto.index,
      // NOTE: not supported yet
      // coupon: null,
      // discount: null,
    } satisfies Ga4Product as Ga4Product;

    // for product attached to basket lines
    setList(item, dto.attributionListName);

    if (dto.category) {
      const categories = (dto.category || "").split("/");

      if (categories.length > 0) {
        item.item_category = categories.at(0);
      }
      if (categories.length > 1) {
        item.item_category2 = categories.at(1);
      }
      if (categories.length > 2) {
        item.item_category3 = categories.at(2);
      }
      if (categories.length > 3) {
        item.item_category4 = categories.at(3);
      }
      if (categories.length > 4) {
        item.item_category5 = categories.at(4);
      }
    }

    return item;
  }

  /**
   * Track a custom event in Google Tag Manager.
   * The "custom_" prefix will be added automatically.
   * @param eventName
   * The name of the custom event.
   * Naming in the form of `noun-verb`, where verb is current tense, i.e. `favorite-list-create`.
   * Do not include the "custom_" prefix!
   */
  function trackCustomEvent(eventName: CustomEventNames, eventParameter?: any) {
    if (!import.meta.client) {
      return;
    }

    const completeEventName = eventName.startsWith("custom_") ? eventName : `custom_${eventName}`;

    // console.debug("trackEvent", completeEventName, eventParameter);

    if (proxy.dataLayer) {
      proxy.dataLayer.push({ ecommerce: null });
      proxy.dataLayer.push({
        event: completeEventName,
        eventParameter: eventParameter,
        ecommerce: null,
      });
    }
  }

  // Every item in the event data must explicitly be configured as a variable in GTM to be usable in tags and triggers.
  function trackCreativeDisplayEvent(
    creativeName: string,
    eventData: CreativeEventData,
  ) {
    if (!import.meta.client) {
      return;
    }

    // We have a trigger in GTM on the prefix "Creative *"
    const completeEventName = `Creative ${creativeName} - Display`;

    // eslint-disable-next-line no-console
    console.debug("trackCreativeDisplayEvent", completeEventName, eventData);

    if (proxy.dataLayer) {
      proxy.dataLayer.push({
        event: completeEventName,
        ...eventData,
        ecommerce: null,
      });
    }
  }

  // Every item in the event data must explicitly be configured as a variable in GTM to be usable in tags and triggers.
  function trackCreativeClickEvent(
    creativeName: string,
    eventData: CreativeEventData,
  ) {
    if (!import.meta.client) {
      return;
    }

    // We have a trigger in GTM on the prefix "Creative *"
    const completeEventName = `Creative ${creativeName} - Click`;

    // eslint-disable-next-line no-console
    console.debug("trackCreativeClickEvent", completeEventName, eventData);

    if (proxy.dataLayer) {
      proxy.dataLayer.push({
        event: completeEventName,
        ...eventData,
        ecommerce: null,
      });
    }
  }

  function trackEcommerceEvent(
    eventName: PredefinedEventNames,
    ecommerceData: { items: Array<Ga4Product>; currency?: string; value?: number | null } & Record<string, any>,
  ) {
    if (!import.meta.client) {
      return;
    }

    // console.debug("trackEvent", eventName, ecommerceData);

    if (proxy.dataLayer) {
      proxy.dataLayer.push({ ecommerce: null });
      proxy.dataLayer.push({
        event: eventName,
        ecommerce: {
          ...ecommerceData,
          currency: ecommerceData.currency ?? "EUR",
        },
      });
    }
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#search
  function trackSearch(query: string) {
    if (!import.meta.client) {
      return;
    }

    // console.debug("trackSearch", query);

    if (proxy.dataLayer) {
      proxy.dataLayer.push({ ecommerce: null });
      proxy.dataLayer.push({
        event: "search",
        search_term: query,
      });
    };
  }

  function trackProductList(products: Array<ProductDataLayer>, listName: string) {
    const items = products.map(convertToGA4);

    for (const item of items) {
      setList(item, listName, items.indexOf(item));
    }

    trackEcommerceEvent("view_item_list", {
      items: items,
    });
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#view_item_details
  function trackProductDetail(product: ProductDataLayer | null, unitPriceGross?: number, discountPrice?: number, currencyCode?: string) {
    if (!product) {
      return;
    }

    const item = convertToGA4(product);
    item.price = unitPriceGross ?? product.price;
    item.discount = discountPrice;

    setTimeout(() => {
      trackEcommerceEvent("view_item", {
        items: [item],
        currency: currencyCode,
        value: item.price,
      });
    }, 1000);
  }

  function trackAddToCart(products: Array<TrackProductInfo>) {
    const items = products.map((product) => {
      const item = convertToGA4(product.dataLayer);
      item.quantity = product.quantity;
      item.price = product.unitPriceGross;
      item.discount = product.discountAmount;

      setList(item, product.listName, product.index);

      return item;
    });

    trackEcommerceEvent("add_to_cart", {
      items: items,
      currency: products.at(0)?.currencyCode,
      value: items.map(i => (i.quantity ?? 0) * (i.price ?? 0)).reduce((acc: number, curr: number) => acc + curr, 0),
    });
  }

  function trackRemoveFromCart(products: Array<TrackProductInfo>) {
    const items = products.map((product) => {
      const item = convertToGA4(product.dataLayer);
      item.quantity = product.quantity;
      item.price = product.unitPriceGross;
      item.discount = product.discountAmount;

      setList(item, product.listName, product.index);

      return item;
    });

    trackEcommerceEvent("remove_from_cart", {
      items: items,
      currency: products.at(0)?.currencyCode,
      value: items.map(i => (i.quantity ?? 0) * (i.price ?? 0)).reduce((acc: number, curr: number) => acc + curr, 0),
    });
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#begin_checkout
  /**
   * Track that a user has begun a checkout.
   * @param products The products in the cart.
   * @param cartTotalGross The monetary value of the event. Don't include shipping or tax.
   * @param currencyCode The currency code.
   */
  function trackBeginCheckout(products: Array<ProductDataLayer>, cartTotalGross: number, currencyCode?: string) {
    const items = products.map(convertToGA4);

    trackEcommerceEvent("begin_checkout", {
      items: items,
      currency: currencyCode,
      value: cartTotalGross,
    });
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_shipping_info
  /**
   * Track the shipping method used during checkout.
   * @param products The products in the cart.
   * @param cartTotalGross The monetary value of the event. Don't include shipping or tax.
   * @param shippingMethod The selected shipping method.
   * @param currencyCode The currency code (optional).
   */
  function trackCheckoutShippingInfo(products: Array<ProductDataLayer>, cartTotalGross: number, shippingMethod: string, currencyCode?: string) {
    const items = products.map(convertToGA4);

    trackEcommerceEvent("add_shipping_info", {
      items: items,
      currency: currencyCode,
      value: cartTotalGross,
      shipping_tier: shippingMethod,
    });
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_payment_info
  /**
   * Track the payment method used during checkout.
   * @param products The products in the cart.
   * @param cartTotalGross The monetary value of the event. Don't include shipping or tax.
   * @param paymentType The selected payment method.
   * @param currencyCode The currency code (optional).
   */
  function trackCheckoutPaymentInfo(products: Array<ProductDataLayer>, cartTotalGross: number, paymentType: string, currencyCode?: string) {
    const items = products.map(convertToGA4);

    trackEcommerceEvent("add_payment_info", {
      items: items,
      currency: currencyCode,
      value: cartTotalGross,
      payment_type: paymentType,
    });
  }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase
  /**
   * Track when one or more items is purchased by a user.
   * @param transaction The transaction data.
   */
  function trackPurchase(transaction: TransactionInfo) {
    const items = transaction.products.map(convertToGA4);

    trackEcommerceEvent("purchase", {
      items: items,
      transaction_id: transaction.transactionId,
      currency: transaction.currency,
      value: transaction.totalGross,
      shipping: transaction.shippingCost ? transaction.shippingCost : null,
      tax: transaction.tax ? transaction.tax : null,
    });
  }

  // function trackAddToFavorites(products: Array<ProductDataLayer>) {
  //   const items = products.map(convertToGA4);

  //   trackEcommerceEvent("add_to_wishlist", {
  //     items: items,
  //   });
  // }

  // function trackPlanktonProductDataLayerClick(product: ProductDataLayer, listName: string, index: number) {
  //   const item = convertToGA4(product);
  //   setList(item, listName, index);

  //   trackEcommerceEvent("select_item", {
  //     items: [item],
  //   });
  // }

  // https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#begin_checkout
  /**
   * Track that a user has begun a checkout.
   * @param products The products in the cart.
   * @param cartTotalGross The monetary value of the event. Don't include shipping or tax.
   */
  // function trackViewCart(products: Array<ProductDataLayer>, cartTotalGross: number) {
  //   const items = products.map(convertToGA4);

  //   trackEcommerceEvent("view_cart", {
  //     items: items,
  //     value: cartTotalGross,
  //   });
  // }

  return {
    trackCreativeDisplayEvent: trackCreativeDisplayEvent,
    trackCreativeClickEvent: trackCreativeClickEvent,
    trackCustomEvent: trackCustomEvent,
    trackSearch: trackSearch,
    trackProductList: trackProductList,
    trackProductDetail: trackProductDetail,
    trackAddToCart: trackAddToCart,
    trackRemoveFromCart: trackRemoveFromCart,
    trackBeginCheckout: trackBeginCheckout,
    trackCheckoutShippingInfo: trackCheckoutShippingInfo,
    trackCheckoutPaymentInfo: trackCheckoutPaymentInfo,
    trackPurchase: trackPurchase,

    // trackAddToFavorites: trackAddToFavorites,
    // trackPlanktonProductDataLayerClick: trackPlanktonProductDataLayerClick,
    // trackViewCart: trackViewCart,
  };
}

function setList(item: Ga4Product, listName?: string | null, index?: number) {
  item.index = index;

  if (listName) {
    // convert list name to kebab-case
    item.item_list_name = listName;
    item.item_list_id = listName.toLowerCase().replace(/\s+/g, "-");
  }
}
